import cn from 'classnames'
import { type ReactNode, useState } from 'react'

import { AbsolutePopup, PopPaper, TPopupVertical } from 'core-components/library/popup'

import styles from './CockpitGrouping.module.scss'

export interface ICockpitGroupingOption<T extends string> {
  value: T
  label: ReactNode
}
interface IProps<T extends string> {
  value: T
  options: ICockpitGroupingOption<T>[]
  label: ReactNode
  ariaLabel: string
  optionsAriaLabel: string
  heading?: ReactNode
  onChange: (value: T) => void
}

export const CockpitGrouping = <T extends string>({
  value,
  options,
  label,
  ariaLabel,
  optionsAriaLabel,
  heading,
  onChange,
}: IProps<T>) => {
  const [isOpened, setOpened] = useState(false)

  return (
    <AbsolutePopup
      rootClassName={styles.root}
      popupClassName={styles.popupWrap}
      verticalStartPosition={TPopupVertical.Bottom}
      visibilityCallback={(visible) => {
        setOpened(visible)
        return visible
      }}
      popupContent={({ action }) => (
        <PopPaper shadowPosition="auto" className={styles.popup}>
          {heading && <div className={styles.heading}>{heading}</div>}
          <div className={styles.options} role="listbox" aria-label={optionsAriaLabel}>
            {options.map((option) => {
              const isSelected = option.value === value

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={cn(styles.option, { [styles.selected]: isSelected })}
                  onClick={() => {
                    onChange(option.value)
                    setOpened(false)
                    action.close()
                  }}
                >
                  {option.label}
                  {isSelected && <i className="kbase kbase-check" />}
                </button>
              )
            })}
          </div>
        </PopPaper>
      )}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={isOpened}
        aria-label={ariaLabel}
      >
        <span className={styles.label}>{label}</span>
        <span className={styles.value}>
          {options.find((option) => option.value === value)?.label}
        </span>
        <i className={cn('kbase', isOpened ? 'kbase-chevron-top' : 'kbase-chevron-bottom')} />
      </button>
    </AbsolutePopup>
  )
}

CockpitGrouping.displayName = 'CockpitGrouping'
